import { Button, TableItem } from 'components/atoms';
import { MagellanPopups, TableDropdown } from 'components/molecules';
import SendButtonEmail from 'components/molecules/buttons/sendEmail/SendButtonEmail';
import LabeledEmailSend from 'components/molecules/labeledStatus/LabeledEmailSend';
import { Table, TopBar } from 'components/organisms';
import { TopbarPage } from 'components/templates';
import { useInfiniteTable } from 'hooks/useInfiniteTable';
import { useCustomParamId } from 'hooks/useParamId';
import { usePermissionGuard } from 'hooks/usePermissionGuard';
import { useStreamFileDownload } from 'hooks/useStreamFileDownload';
import { t } from 'i18next';
import { PiCaretLeftBold, PiClock, PiDownload } from 'react-icons/pi';
import { useNavigate } from 'react-router-dom';
import { DOCUMENT_TYPE } from 'services/email/types';
import {
  useGetHistoricalReportsByLabelInfinite,
  useGetLabelReportDetailedFile,
  useGetLabelReportDetailedFilePreview,
  useGetLabelReportFile,
  useGetLabelReportFilePreview,
} from 'services/label-report';
import { ILabelReportsByLabelResponse } from 'services/label-report/types';
import { EMAIL_SEND } from 'services/types';
import { magellanColumns } from 'types/components/table/table.types';
import { formatCurrency } from 'utils/global/currency.utils';
import { getMaxFormattedLength } from 'utils/global/getMaxFormattedLength.utils';
import { getSettlementPeriodName } from 'utils/global/name.utils';

const LabelReportsHistory = () => {
  const labelId = useCustomParamId('id', '/label');

  usePermissionGuard('LabelReportOrderPermissionsLIST**');

  const navigate = useNavigate();

  const labelReports = useGetHistoricalReportsByLabelInfinite(labelId, {
    itemCount: 25,
  });

  const tableParams = useInfiniteTable(labelReports, { selectable: false });

  const { setFile } = useStreamFileDownload(useGetLabelReportFile);
  const { setFile: setDetailedFile } = useStreamFileDownload(
    useGetLabelReportDetailedFile
  );

  const allAmountLength = getMaxFormattedLength(
    tableParams.data?.nodes ?? [],
    e => e?.amountEarned
  );

  const allAmountLabelLength = getMaxFormattedLength(
    tableParams.data?.nodes ?? [],
    e => e?.amountForLabel
  );

  return (
    <TopbarPage
      topbar={
        <TopBar
          title={t('history.label-raports')}
          size='compact'
          customBackItem={
            <Button
              isClear
              onlyIcon
              onClick={() => navigate(`/label/${labelId}/reports`)}>
              <Button.Icon icon={<PiCaretLeftBold />} />
            </Button>
          }
        />
      }>
      <Table
        id={'LabelReportsHistory'}
        {...tableParams}
        size='compact'
        columns={magellanColumns<ILabelReportsByLabelResponse>([
          {
            label: t('input.label.startSettlementPeriod'),
            renderCell: ({ startSettlementPeriodName }) => (
              <TableItem>
                <TableItem.Text
                  text={getSettlementPeriodName(startSettlementPeriodName)}
                />
              </TableItem>
            ),
            resize: true,
            minWidth: 160,
          },
          {
            label: t('input.label.endSettlementPeriod'),
            renderCell: ({ endSettlementPeriodName }) => (
              <TableItem>
                <TableItem.Text
                  text={getSettlementPeriodName(endSettlementPeriodName)}
                />
              </TableItem>
            ),
            resize: true,
            minWidth: 160,
          },
          {
            label: t('input.label.number'),
            renderCell: ({ labelReportNumber }) => (
              <TableItem>
                <TableItem.Text text={labelReportNumber ?? undefined} />
              </TableItem>
            ),
            resize: true,
            minWidth: 140,
          },
          {
            label: t('input.label.amountEarned'),
            renderCell: ({ amountEarned }) => (
              <TableItem>
                <TableItem.Currency
                  text={formatCurrency(amountEarned, {
                    length: allAmountLength,
                  })}
                />
              </TableItem>
            ),
            resize: true,
          },
          {
            label: t('input.label.amountForLabel'),
            renderCell: ({ amountForLabel }) => (
              <TableItem>
                <TableItem.Currency
                  text={formatCurrency(amountForLabel, {
                    length: allAmountLabelLength,
                  })}
                />
              </TableItem>
            ),
            resize: true,
          },
          {
            label: t('label.table.compliance'),
            renderCell: ({ isOutdated }) => (
              <TableItem>
                {isOutdated && (
                  <TableItem.Label color='orange' size='compact'>
                    <PiClock />
                    {t('label.compliance.outdated')}
                  </TableItem.Label>
                )}
              </TableItem>
            ),
            resize: true,
            minWidth: 140,
          },
          {
            label: t('input.label.email-sender'),
            renderCell: ({ id, sendStatus }) => (
              <TableItem>
                <div className='flex items-center gap-2'>
                  {sendStatus !== EMAIL_SEND.notSend && (
                    <LabeledEmailSend status={sendStatus} />
                  )}
                  {sendStatus !== EMAIL_SEND.planned && (
                    <SendButtonEmail
                      documentId={id}
                      documentType={DOCUMENT_TYPE.labelReport}
                      refetch={labelReports.refetch}
                      status={sendStatus}
                    />
                  )}
                </div>
              </TableItem>
            ),
            resize: true,
            minWidth: 180,
          },
          {
            label: '',
            minWidth: 56,
            renderCell: ({
              id,
              filePath,
              labelReportNumber,
              detailedFilePath,
            }) => {
              const reportName = labelReportNumber ?? filePath ?? '';

              return (
                <TableItem justifyPosition='end'>
                  <TableDropdown justifyPosition='end' style={{ padding: 0 }}>
                    {detailedFilePath ? (
                      <>
                        <MagellanPopups.FilePreviewPopup
                          fileId={id}
                          fileName={`${reportName}_${t('button.simple')}`}
                          fileService={useGetLabelReportFilePreview}
                          trigger={
                            <Button
                              type='button'
                              isClear
                              fullWidth
                              size='compact'
                              buttonStyle={{ justifyContent: 'start' }}>
                              <Button.Icon icon={<PiDownload />} />
                              <Button.Text
                                text={t('button.download-variant', {
                                  variant: t('button.simple'),
                                })}
                              />
                            </Button>
                          }
                          onDownload={() =>
                            setFile({
                              id,
                              name: `${reportName}_${t('button.simple')}`,
                            })
                          }
                        />
                        <MagellanPopups.FilePreviewPopup
                          fileId={id}
                          fileName={`${reportName}_${t('button.detail')}`}
                          fileService={useGetLabelReportDetailedFilePreview}
                          trigger={
                            <Button
                              type='button'
                              isClear
                              fullWidth
                              size='compact'
                              buttonStyle={{ justifyContent: 'start' }}>
                              <Button.Icon icon={<PiDownload />} />
                              <Button.Text
                                text={t('button.download-variant', {
                                  variant: t('button.detail'),
                                })}
                              />
                            </Button>
                          }
                          onDownload={() =>
                            setDetailedFile({
                              id,
                              name: `${reportName}_${t('button.detail')}`,
                            })
                          }
                        />
                      </>
                    ) : (
                      <MagellanPopups.FilePreviewPopup
                        fileId={id}
                        fileName={reportName}
                        fileService={useGetLabelReportFilePreview}
                        trigger={
                          <Button
                            type='button'
                            isClear
                            fullWidth
                            size='compact'
                            buttonStyle={{ justifyContent: 'start' }}>
                            <Button.Icon icon={<PiDownload />} />
                            <Button.Text text={t('button.download')} />
                          </Button>
                        }
                        onDownload={() => setFile({ id, name: reportName })}
                      />
                    )}
                  </TableDropdown>
                </TableItem>
              );
            },
          },
        ])}
      />
    </TopbarPage>
  );
};

export default LabelReportsHistory;
